import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useParams } from 'react-router-dom';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// ──────────────────────────────────────
// Candidate Interview Page
// Full-screen interview experience with:
// - One question at a time
// - MCQ / Text / Code answer types
// - Section-based grouping
// - Progress bar + timer
// - Review & submit
// ──────────────────────────────────────

const CandidateInterviewPage = () => {
  const { token } = useParams();

  // ── State ──
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [interview, setInterview] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [sections, setSections] = useState([]);
  const [responses, setResponses] = useState({});
  const [currentIndex, setCurrentIndex] = useState(0);
  const [phase, setPhase] = useState('intro'); // intro | answering | review | submitted
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [autoSaveMsg, setAutoSaveMsg] = useState('');
  const [score, setScore] = useState(null);
  const timerRef = useRef(null);
  const autoSaveRef = useRef(null);

  // ── Fetch interview data ──
  useEffect(() => {
    const fetchInterview = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/interview/take/${token}`);
        const data = await res.json();
        if (!res.ok || !data.success) {
          setError(data.error || data.message || 'Failed to load interview');
          setLoading(false);
          return;
        }
        setInterview(data);
        setQuestions(data.questions || []);
        setSections(data.sections || []);
        setResponses(data.existing_responses || {});
        setTimeRemaining(data.total_time_seconds || 3600);
        setLoading(false);
      } catch (err) {
        setError(err.message || 'Network error');
        setLoading(false);
      }
    };
    if (token) fetchInterview();
  }, [token]);

  // ── Timer ──
  useEffect(() => {
    if (phase !== 'answering') return;
    timerRef.current = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          handleSubmit(); // Auto-submit when time runs out
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  // ── Auto-save every 30 seconds ──
  useEffect(() => {
    if (phase !== 'answering') return;
    autoSaveRef.current = setInterval(() => {
      autoSave();
    }, 30000);
    return () => clearInterval(autoSaveRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, responses]);

  const autoSave = useCallback(async () => {
    if (!token || Object.keys(responses).length === 0) return;
    try {
      setSaving(true);
      await fetch(`${API_BASE_URL}/interview/take/${token}/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ responses })
      });
      setAutoSaveMsg('Auto-saved');
      setTimeout(() => setAutoSaveMsg(''), 2000);
    } catch (e) { /* silent */ }
    finally { setSaving(false); }
  }, [token, responses]);

  // ── Save answer for a question ──
  const setAnswer = useCallback((qNum, value) => {
    setResponses(prev => ({
      ...prev,
      [qNum]: { ...prev[qNum], ...value, answered_at: new Date().toISOString() }
    }));
  }, []);

  // ── Navigation ──
  const currentQuestion = questions[currentIndex] || null;
  const totalQuestions = questions.length;

  const goNext = () => { if (currentIndex < totalQuestions - 1) setCurrentIndex(currentIndex + 1); };
  const goPrev = () => { if (currentIndex > 0) setCurrentIndex(currentIndex - 1); };
  const goToQuestion = (idx) => { setCurrentIndex(idx); setPhase('answering'); };

  // ── Current section info ──
  const currentSection = useMemo(() => {
    if (!currentQuestion) return null;
    return sections.find(s => s.questions?.some(q => q.question_number === currentQuestion.question_number));
  }, [currentQuestion, sections]);

  // ── Answered count ──
  const answeredCount = useMemo(() => {
    return questions.filter(q => {
      const r = responses[q.question_number];
      if (!r) return false;
      if (q.answer_type === 'mcq') return r.selected_option !== undefined && r.selected_option !== null;
      return r.answer && r.answer.trim().length > 0;
    }).length;
  }, [questions, responses]);

  // ── Format time ──
  const formatTime = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  // ── Submit ──
  const handleSubmit = useCallback(async () => {
    try {
      setSubmitting(true);
      clearInterval(timerRef.current);
      clearInterval(autoSaveRef.current);
      const res = await fetch(`${API_BASE_URL}/interview/take/${token}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ responses })
      });
      const data = await res.json();
      if (data.success) {
        setScore(data.score);
        setPhase('submitted');
      } else {
        alert(data.error || 'Submission failed');
      }
    } catch (e) {
      alert('Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }, [token, responses]);

  // ── Start interview ──
  const startInterview = () => {
    setPhase('answering');
    setCurrentIndex(0);
  };

  // ── Difficulty color ──
  const difficultyColor = (d) => {
    const l = (d || '').toLowerCase();
    if (l === 'easy') return { bg: '#ECFDF5', text: '#059669', border: '#A7F3D0' };
    if (l === 'medium') return { bg: '#FFFBEB', text: '#D97706', border: '#FDE68A' };
    return { bg: '#FEF2F2', text: '#DC2626', border: '#FECACA' };
  };

  // ── Loading / Error states ──
  if (loading) return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-gray-600 font-medium">Loading your interview...</p>
      </div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-red-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </div>
        <h2 className="text-xl font-bold text-gray-900 mb-2">Unable to Load Interview</h2>
        <p className="text-gray-600">{error}</p>
      </div>
    </div>
  );

  // ══════════════════════════════════════
  //  PHASE: INTRO
  // ══════════════════════════════════════
  if (phase === 'intro') return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-2xl w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-1">AI Technical Interview</h1>
          <p className="text-gray-500">{interview?.job_title || 'Position'}</p>
        </div>

        {/* Interview info */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          <InfoCard icon="📝" label="Questions" value={totalQuestions} />
          <InfoCard icon="⏱️" label="Duration" value={`${Math.ceil((interview?.total_time_seconds || 3600) / 60)} min`} />
          <InfoCard icon="📂" label="Sections" value={sections.length} />
          <InfoCard icon="🎯" label="MCQs" value={questions.filter(q => q.answer_type === 'mcq').length} />
        </div>

        {/* Sections preview */}
        <div className="mb-8">
          <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">Interview Sections</h3>
          <div className="space-y-2">
            {sections.map((sec, i) => (
              <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <span className="w-7 h-7 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold">{i + 1}</span>
                  <span className="text-sm font-medium text-gray-800">{sec.name}</span>
                </div>
                <span className="text-xs text-gray-500">{sec.question_count} question{sec.question_count !== 1 ? 's' : ''}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Instructions */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-8">
          <h4 className="text-sm font-semibold text-amber-800 mb-2">📋 Instructions</h4>
          <ul className="text-sm text-amber-700 space-y-1.5">
            <li>• Answer each question to the best of your ability</li>
            <li>• For MCQ questions, select the best option</li>
            <li>• For coding questions, write clean and working code</li>
            <li>• Your progress is auto-saved every 30 seconds</li>
            <li>• You can navigate between questions freely</li>
            <li>• Review all answers before final submission</li>
            <li>• The timer starts once you click "Start Interview"</li>
          </ul>
        </div>

        {/* Welcome message */}
        {interview?.header?.candidate_name && (
          <p className="text-center text-gray-600 mb-6">
            Welcome, <strong>{interview.header.candidate_name}</strong>! Good luck with your interview.
          </p>
        )}

        <button
          onClick={startInterview}
          className="w-full py-3.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl transition-all hover:shadow-lg text-lg"
        >
          🚀 Start Interview
        </button>
      </div>
    </div>
  );

  // ══════════════════════════════════════
  //  PHASE: SUBMITTED
  // ══════════════════════════════════════
  if (phase === 'submitted') return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-green-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-lg w-full text-center">
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Interview Submitted!</h2>
        <p className="text-gray-600 mb-6">Thank you for completing the interview. Your responses have been recorded.</p>

        {score && (
          <div className="bg-gray-50 rounded-xl p-6 mb-6">
            <div className="text-4xl font-bold mb-2" style={{ color: score.percentage >= 70 ? '#059669' : score.percentage >= 40 ? '#D97706' : '#DC2626' }}>
              {score.percentage}%
            </div>
            <p className="text-sm text-gray-500">{score.total} / {score.max} points (auto-scored MCQs)</p>
            <p className="text-xs text-gray-400 mt-1">Text and code answers will be reviewed by the recruiter</p>
          </div>
        )}

        <p className="text-sm text-gray-500">You can close this window now. The recruiter will review your responses.</p>
      </div>
    </div>
  );

  // ══════════════════════════════════════
  //  PHASE: REVIEW
  // ══════════════════════════════════════
  if (phase === 'review') return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <h2 className="font-bold text-gray-900">📋 Review Your Answers</h2>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-500">{answeredCount}/{totalQuestions} answered</span>
            <button
              onClick={() => setPhase('answering')}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              ← Back to Questions
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="px-6 py-2 text-sm font-semibold text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              {submitting ? 'Submitting...' : '✅ Submit Interview'}
            </button>
          </div>
        </div>
      </div>

      {/* Review list */}
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-4">
        {sections.map((sec, si) => (
          <div key={si} className="bg-white rounded-xl border overflow-hidden">
            <div className="px-4 py-3 bg-gray-50 border-b">
              <h3 className="font-semibold text-gray-800">{sec.name}</h3>
              <p className="text-xs text-gray-500">{sec.description}</p>
            </div>
            <div className="divide-y">
              {sec.questions.map((q) => {
                const r = responses[q.question_number];
                const isAnswered = q.answer_type === 'mcq'
                  ? (r?.selected_option !== undefined && r?.selected_option !== null)
                  : (r?.answer && r.answer.trim().length > 0);
                const dc = difficultyColor(q.difficulty);

                return (
                  <div
                    key={q.question_number}
                    className="px-4 py-3 flex items-start gap-3 hover:bg-gray-50 cursor-pointer"
                    onClick={() => goToQuestion(questions.findIndex(qq => qq.question_number === q.question_number))}
                  >
                    <span className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${isAnswered ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'}`}>
                      {isAnswered ? '✓' : q.question_number}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-800 line-clamp-1">{q.question}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs px-1.5 py-0.5 rounded" style={{ backgroundColor: dc.bg, color: dc.text }}>{q.difficulty}</span>
                        <span className="text-xs text-gray-400">{q.answer_type.toUpperCase()}</span>
                        {isAnswered ? (
                          <span className="text-xs text-green-600">✓ Answered</span>
                        ) : (
                          <span className="text-xs text-red-500">○ Not answered</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        {/* Unanswered warning */}
        {answeredCount < totalQuestions && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <p className="text-sm text-amber-700 font-medium">
              ⚠️ You have {totalQuestions - answeredCount} unanswered question(s). You can still submit, but unanswered questions will receive 0 marks.
            </p>
          </div>
        )}

        <div className="flex justify-center pt-4 pb-12">
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="px-10 py-3 text-base font-semibold text-white bg-green-600 rounded-xl hover:bg-green-700 disabled:opacity-50 hover:shadow-lg transition-all"
          >
            {submitting ? 'Submitting...' : '✅ Submit Interview'}
          </button>
        </div>
      </div>
    </div>
  );

  // ══════════════════════════════════════
  //  PHASE: ANSWERING (main interview)
  // ══════════════════════════════════════
  const progressPercent = totalQuestions > 0 ? Math.round(((currentIndex + 1) / totalQuestions) * 100) : 0;
  const dc = difficultyColor(currentQuestion?.difficulty);
  const timeWarning = timeRemaining < 300; // < 5 min

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* ── Top Bar ── */}
      <div className="bg-white border-b shadow-sm sticky top-0 z-20">
        <div className="max-w-5xl mx-auto px-4 py-2">
          {/* Row 1: Info + Timer */}
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <span className="text-sm font-semibold text-gray-900">{interview?.job_title || 'Interview'}</span>
              {currentSection && (
                <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full font-medium">
                  {currentSection.name}
                </span>
              )}
            </div>
            <div className="flex items-center gap-3">
              {autoSaveMsg && <span className="text-xs text-green-600">✓ {autoSaveMsg}</span>}
              {saving && <span className="text-xs text-gray-400">Saving...</span>}
              <div className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-mono font-bold ${timeWarning ? 'bg-red-100 text-red-700 animate-pulse' : 'bg-gray-100 text-gray-700'}`}>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {formatTime(timeRemaining)}
              </div>
            </div>
          </div>

          {/* Row 2: Progress bar */}
          <div className="flex items-center gap-3">
            <span className="text-xs font-medium text-gray-500 whitespace-nowrap">
              Q {currentIndex + 1} / {totalQuestions}
            </span>
            <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-blue-500 rounded-full transition-all duration-300"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
            <span className="text-xs font-medium text-gray-500 whitespace-nowrap">
              {answeredCount} answered
            </span>
          </div>
        </div>
      </div>

      {/* ── Main Content ── */}
      <div className="flex-1 flex">
        {/* Question navigator sidebar (desktop) */}
        <div className="hidden lg:block w-64 bg-white border-r overflow-y-auto p-3">
          <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Questions</h4>
          <div className="space-y-1">
            {questions.map((q, idx) => {
              const r = responses[q.question_number];
              const isAnswered = q.answer_type === 'mcq'
                ? (r?.selected_option !== undefined && r?.selected_option !== null)
                : (r?.answer && r.answer.trim().length > 0);
              const isCurrent = idx === currentIndex;
              const d = difficultyColor(q.difficulty);

              return (
                <button
                  key={idx}
                  onClick={() => setCurrentIndex(idx)}
                  className={`w-full text-left flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs transition-all ${
                    isCurrent
                      ? 'bg-blue-50 border border-blue-200'
                      : isAnswered
                        ? 'bg-green-50 border border-green-100'
                        : 'hover:bg-gray-50 border border-transparent'
                  }`}
                >
                  <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
                    isCurrent ? 'bg-blue-600 text-white'
                    : isAnswered ? 'bg-green-500 text-white'
                    : 'bg-gray-200 text-gray-500'
                  }`}>
                    {isAnswered && !isCurrent ? '✓' : q.question_number}
                  </span>
                  <span className={`flex-1 truncate ${isCurrent ? 'text-blue-800 font-medium' : 'text-gray-600'}`}>
                    Q{q.question_number}
                  </span>
                  <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: d.text }}></span>
                </button>
              );
            })}
          </div>
        </div>

        {/* ── Question Card ── */}
        <div className="flex-1 flex items-start justify-center p-4 sm:p-8 overflow-y-auto">
          <div className="w-full max-w-3xl">
            {currentQuestion && (
              <div className="bg-white rounded-2xl shadow-sm border p-6 sm:p-8">
                {/* Question header */}
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: dc.bg, color: dc.text, border: `1px solid ${dc.border}` }}>
                        {currentQuestion.difficulty}
                      </span>
                      <span className="text-xs font-medium text-gray-400 bg-gray-100 px-2 py-0.5 rounded">
                        {currentQuestion.answer_type === 'mcq' ? 'Multiple Choice' : currentQuestion.answer_type === 'code' ? 'Coding' : 'Written Answer'}
                      </span>
                      {currentQuestion.skill && (
                        <span className="text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded">{currentQuestion.skill}</span>
                      )}
                      {currentQuestion.section && (
                        <span className="text-xs text-purple-600 bg-purple-50 px-2 py-0.5 rounded">{currentQuestion.section}</span>
                      )}
                    </div>
                    <h2 className="text-lg font-semibold text-gray-900 leading-relaxed">
                      {currentQuestion.question}
                    </h2>
                  </div>
                  <span className="flex-shrink-0 w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-sm font-bold text-gray-500">
                    {currentQuestion.question_number}
                  </span>
                </div>

                {/* ── Answer Area ── */}
                <div className="mt-4">
                  {currentQuestion.answer_type === 'mcq' && currentQuestion.options ? (
                    <MCQOptions
                      options={currentQuestion.options}
                      selected={responses[currentQuestion.question_number]?.selected_option}
                      onChange={(idx) => setAnswer(currentQuestion.question_number, { selected_option: idx })}
                    />
                  ) : currentQuestion.answer_type === 'code' ? (
                    <CodeEditor
                      language={currentQuestion.code_language}
                      value={responses[currentQuestion.question_number]?.answer || ''}
                      onChange={(val) => setAnswer(currentQuestion.question_number, { answer: val })}
                    />
                  ) : (
                    <TextAnswer
                      value={responses[currentQuestion.question_number]?.answer || ''}
                      onChange={(val) => setAnswer(currentQuestion.question_number, { answer: val })}
                    />
                  )}
                </div>

                {/* Time limit hint */}
                {currentQuestion.time_limit_seconds && (
                  <p className="mt-4 text-xs text-gray-400">
                    ⏱️ Suggested time: {Math.ceil(currentQuestion.time_limit_seconds / 60)} min
                  </p>
                )}
              </div>
            )}

            {/* Navigation */}
            <div className="flex items-center justify-between mt-6">
              <button
                onClick={goPrev}
                disabled={currentIndex === 0}
                className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border rounded-xl hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Previous
              </button>

              {/* Mobile question dots */}
              <div className="flex items-center gap-1 lg:hidden overflow-x-auto max-w-[200px]">
                {questions.map((q, idx) => {
                  const r = responses[q.question_number];
                  const isAnswered = q.answer_type === 'mcq'
                    ? (r?.selected_option !== undefined && r?.selected_option !== null)
                    : (r?.answer && r.answer.trim().length > 0);
                  return (
                    <button
                      key={idx}
                      onClick={() => setCurrentIndex(idx)}
                      className={`w-2.5 h-2.5 rounded-full flex-shrink-0 transition-all ${
                        idx === currentIndex ? 'bg-blue-600 scale-125'
                        : isAnswered ? 'bg-green-500'
                        : 'bg-gray-300'
                      }`}
                    />
                  );
                })}
              </div>

              {currentIndex === totalQuestions - 1 ? (
                <button
                  onClick={() => { autoSave(); setPhase('review'); }}
                  className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-green-600 rounded-xl hover:bg-green-700 transition-all"
                >
                  Review & Submit
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </button>
              ) : (
                <button
                  onClick={goNext}
                  className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-blue-600 rounded-xl hover:bg-blue-700 transition-all"
                >
                  Next
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};


// ══════════════════════════════════════
//  SUB-COMPONENTS
// ══════════════════════════════════════

const InfoCard = ({ icon, label, value }) => (
  <div className="bg-gray-50 rounded-xl p-3 text-center">
    <span className="text-2xl">{icon}</span>
    <p className="text-lg font-bold text-gray-900 mt-1">{value}</p>
    <p className="text-xs text-gray-500">{label}</p>
  </div>
);

/**
 * MCQ Options with radio buttons
 */
const MCQOptions = ({ options, selected, onChange }) => {
  const letters = ['A', 'B', 'C', 'D'];
  return (
    <div className="space-y-3">
      {options.map((opt, idx) => {
        const isSelected = selected === idx;
        return (
          <button
            key={idx}
            onClick={() => onChange(idx)}
            className={`w-full flex items-center gap-3 p-4 rounded-xl border-2 text-left transition-all ${
              isSelected
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
            }`}
          >
            <span className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
              isSelected ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'
            }`}>
              {letters[idx]}
            </span>
            <span className={`text-sm ${isSelected ? 'text-blue-900 font-medium' : 'text-gray-700'}`}>
              {opt.label}
            </span>
            <div className="ml-auto flex-shrink-0">
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                isSelected ? 'border-blue-600' : 'border-gray-300'
              }`}>
                {isSelected && <div className="w-2.5 h-2.5 rounded-full bg-blue-600"></div>}
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
};

/**
 * Text area for open-ended questions
 */
const TextAnswer = ({ value, onChange }) => (
  <div>
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      rows={8}
      className="w-full p-4 border-2 border-gray-200 rounded-xl text-sm text-gray-800 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none resize-y transition-all placeholder-gray-400"
      placeholder="Type your answer here... Be detailed and specific."
    />
    <p className="mt-1 text-xs text-gray-400 text-right">{value.length} characters</p>
  </div>
);

/**
 * Code editor (simple textarea with monospace font)
 */
const CodeEditor = ({ language, value, onChange }) => (
  <div>
    {language && (
      <div className="flex items-center gap-2 mb-2">
        <span className="text-xs font-mono bg-gray-800 text-green-400 px-2 py-0.5 rounded">{language}</span>
        <span className="text-xs text-gray-400">Write your solution below</span>
      </div>
    )}
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      rows={12}
      className="w-full p-4 bg-gray-900 text-green-400 border-2 border-gray-700 rounded-xl text-sm font-mono focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none resize-y transition-all placeholder-gray-600"
      placeholder={`// Write your ${language || 'code'} solution here...\n`}
      spellCheck={false}
    />
    <p className="mt-1 text-xs text-gray-400 text-right">{value.split('\n').length} lines</p>
  </div>
);


export default CandidateInterviewPage;
